/**
 * HotelCard Component
 * Displays a single hotel recommendation with details and booking options
 */

import { MapPin, Star, ExternalLink } from 'lucide-react';
import UberButton from './UberButton';

// Map amenity strings to simple display labels
const AMENITY_ICONS = {
  'free wifi': '📶',
  'wifi': '📶',
  'pool': '🏊',
  'swimming pool': '🏊',
  'fitness center': '🏋️',
  'gym': '🏋️',
  'parking': '🅿️',
  'free parking': '🅿️',
  'restaurant': '🍽️',
  'bar': '🍸',
  'business center': '💼',
  'room service': '🛎️',
  'spa': '💆',
  'airport shuttle': '🚌',
  'pet friendly': '🐾',
  'hot tub': '♨️',
  'laundry': '👔',
  'concierge': '🛎️',
};

function getAmenityEmoji(amenity) {
  const key = amenity.toLowerCase();
  for (const [pattern, emoji] of Object.entries(AMENITY_ICONS)) {
    if (key.includes(pattern)) return emoji;
  }
  return '✓';
}

function renderStars(count) {
  const stars = Math.min(5, Math.max(1, Math.round(count)));
  return '★'.repeat(stars) + '☆'.repeat(5 - stars);
}

const HotelCard = ({ hotel, trip }) => {
  if (!hotel) return null;

  const handleBookClick = () => {
    // Include the address so Google finds the right property, not just a name match
    const query = [hotel.name, hotel.address, hotel.city]
      .filter(Boolean)
      .join(' ');
    const url = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleMapClick = () => {
    // Prefer placeId deep-link (exact pin) over text search
    const url = hotel.placeId
      ? `https://www.google.com/maps/place/?q=place_id:${hotel.placeId}`
      : `https://www.google.com/maps/search/${encodeURIComponent(
          hotel.address && hotel.city
            ? `${hotel.address}, ${hotel.city}, ${hotel.state || ''}`
            : `${hotel.name} ${hotel.city}`
        )}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const checkInDate = trip?.startDate
    ? new Date(trip.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  const amenitiesToShow = (hotel.amenities || []).slice(0, 6);

  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: '10px',
      padding: '14px',
      marginBottom: '12px',
      backgroundColor: '#ffffff',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
    }}>
      {/* Header Row: Name + Rating */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '4px'
      }}>
        <div style={{ flex: 1, marginRight: '8px' }}>
          <h3 style={{
            margin: 0,
            fontSize: '15px',
            fontWeight: '700',
            color: '#1f2937',
            lineHeight: '1.3'
          }}>
            {hotel.name}
          </h3>
          <div style={{
            fontSize: '12px',
            color: '#6b7280',
            marginTop: '2px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ color: '#f59e0b', fontWeight: '600', letterSpacing: '-1px' }}>
              {renderStars(hotel.starRating)}
            </span>
            <span>{hotel.brand}</span>
          </div>
        </div>

        {/* Price + Rating bubble */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1f2937'
          }}>
            ${hotel.pricePerNight}
          </div>
          <div style={{ fontSize: '11px', color: '#6b7280' }}>per night</div>
          <div style={{
            marginTop: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '3px',
            fontSize: '12px'
          }}>
            <Star size={12} fill="#FFD700" color="#FFD700" />
            <span style={{ fontWeight: '600' }}>{hotel.rating?.toFixed(1)}</span>
            {hotel.reviewCount && (
              <span style={{ color: '#9ca3af' }}>({hotel.reviewCount})</span>
            )}
          </div>
        </div>
      </div>

      {/* Check-in date if available */}
      {checkInDate && (
        <div style={{
          fontSize: '12px',
          color: '#2563eb',
          marginBottom: '6px',
          fontWeight: '500'
        }}>
          📅 Check-in {checkInDate}
        </div>
      )}

      {/* Description */}
      {hotel.description && (
        <p style={{
          fontSize: '13px',
          color: '#4b5563',
          margin: '6px 0',
          lineHeight: '1.4'
        }}>
          {hotel.description}
        </p>
      )}

      {/* Address — tap to verify on Maps */}
      {hotel.address && (
        <div
          onClick={handleMapClick}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '5px',
            marginBottom: '8px',
            fontSize: '12px',
            color: '#2563eb',
            cursor: 'pointer',
            textDecoration: 'underline',
            textDecorationColor: '#bfdbfe'
          }}
          title="Verify on Google Maps"
        >
          <MapPin size={13} style={{ marginTop: '1px', flexShrink: 0 }} />
          <span style={{ flex: 1 }}>
            {hotel.address}
            {hotel.distanceToDowntown > 0 && (
              <span style={{ marginLeft: '6px', color: '#9ca3af', textDecoration: 'none' }}>
                · {hotel.distanceToDowntown.toFixed(1)} mi to downtown
              </span>
            )}
          </span>
        </div>
      )}

      {/* Amenity chips */}
      {amenitiesToShow.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '5px',
          marginBottom: '10px'
        }}>
          {amenitiesToShow.map((amenity, i) => (
            <span key={i} style={{
              padding: '2px 8px',
              backgroundColor: '#f3f4f6',
              borderRadius: '999px',
              fontSize: '11px',
              color: '#374151',
              whiteSpace: 'nowrap'
            }}>
              {getAmenityEmoji(amenity)} {amenity}
            </span>
          ))}
        </div>
      )}

      {/* Why book here */}
      {hotel.recommendedFor && (
        <div style={{
          fontSize: '12px',
          color: '#059669',
          fontStyle: 'italic',
          marginBottom: '10px'
        }}>
          💡 {hotel.recommendedFor}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Uber button */}
        {hotel.address && (
          <UberButton
            destination={hotel.address}
            destinationName={hotel.name}
          />
        )}

        {/* Verify on Maps button */}
        <button
          onClick={handleMapClick}
          style={{
            padding: '7px 10px',
            backgroundColor: '#ffffff',
            color: '#374151',
            border: '1.5px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'border-color 0.2s',
            whiteSpace: 'nowrap'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6b7280'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; }}
        >
          <MapPin size={12} />
          Map
        </button>

        {/* Book on Google button */}
        <button
          onClick={handleBookClick}
          style={{
            flex: 1,
            padding: '7px 12px',
            backgroundColor: '#2563eb',
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1d4ed8'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }}
        >
          <ExternalLink size={13} />
          Book on Google
        </button>
      </div>

      {/* Source badge */}
      {hotel.source === 'google-places' ? (
        <div style={{
          fontSize: '11px',
          color: '#059669',
          marginTop: '8px',
          textAlign: 'center',
          fontWeight: '500'
        }}>
          ✓ Verified by Google Maps
        </div>
      ) : (
        <div style={{
          fontSize: '10px',
          color: '#9ca3af',
          marginTop: '8px',
          textAlign: 'center'
        }}>
          AI-suggested · Tap Map to verify location before booking
        </div>
      )}
    </div>
  );
};

export default HotelCard;
